import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { adminApi, currency } from "@/lib/api";
import { StatusBadge } from "@/pages/Reservations";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

const STATUSES = ["pending", "confirmed", "in_progress", "completed", "cancelled"];

export default function AdminOrders() {
  const qc = useQueryClient();
  const { data: orders = [] } = useQuery({ queryKey: ["admin-orders"], queryFn: async () => (await adminApi.get("/admin/orders")).data });

  const updateStatus = async (id, status) => {
    try {
      await adminApi.put(`/admin/orders/${id}`, { status });
      toast.success("Status updated");
      qc.invalidateQueries({ queryKey: ["admin-orders"] });
      qc.invalidateQueries({ queryKey: ["analytics"] });
    } catch { toast.error("Update failed"); }
  };

  const setPayment = async (id, patch) => {
    try {
      await adminApi.put(`/admin/orders/${id}/payment`, patch);
      toast.success("Payment status updated");
      qc.invalidateQueries({ queryKey: ["admin-orders"] });
      qc.invalidateQueries({ queryKey: ["analytics"] });
    } catch { toast.error("Update failed"); }
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="overline-label mb-1">Fulfilment</p>
        <h1 className="font-serif text-3xl text-cream">Orders <span className="text-muted2 text-lg">({orders.length})</span></h1>
      </div>

      {orders.length === 0 ? (
        <p className="text-muted2">No orders yet.</p>
      ) : (
        <Accordion type="single" collapsible data-testid="admin-orders-data-table" className="space-y-3">
          {orders.map((o) => (
            <AccordionItem key={o.id} value={o.id} data-testid={`admin-order-${o.id}`} className="gold-card !border-gold/20 px-5">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex flex-wrap items-center justify-between gap-3 w-full pr-4">
                  <div className="text-left">
                    <p className="text-cream text-sm">{o.customer_name || o.customer_email}</p>
                    <p className="text-xs text-muted2 font-mono">#{o.id.slice(-8)} · {o.event_start}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-gold font-serif">{currency(o.total)}</span>
                    <StatusBadge status={o.status} />
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-2 border-t border-gold/15 pt-4">
                  {o.items.map((i, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span className="text-cream/80">{i.name} × {i.quantity} · {i.days}d</span>
                      <span className="text-muted2">{currency(i.price_per_day * i.quantity * i.days)}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-muted2">
                  <p><span className="text-gold">Address:</span> {o.delivery_address}</p>
                  <p><span className="text-gold">Phone:</span> {o.contact_phone}</p>
                  <p><span className="text-gold">Dates:</span> {o.event_start} → {o.event_end}</p>
                  <p><span className="text-gold">Email:</span> {o.customer_email}</p>
                  {o.notes && <p className="sm:col-span-2"><span className="text-gold">Notes:</span> {o.notes}</p>}
                </div>
                <div className="mt-5 flex items-center gap-3">
                  <span className="text-xs text-muted2">Update status:</span>
                  <Select value={o.status} onValueChange={(v) => updateStatus(o.id, v)}>
                    <SelectTrigger data-testid={`admin-order-status-${o.id}`} className="w-48 bg-ink border-gold/25 text-cream"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-ink-800 border-gold/30 text-cream">
                      {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="mt-5 pt-4 border-t border-gold/15">
                  <p className="text-xs text-muted2 mb-3">Payments — total {currency(o.total)} · <span className="text-gold">{(o.payment_status || "unpaid").replace("_", " ")}</span></p>
                  <div className="flex flex-wrap gap-3">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-cream/80">Deposit {currency(o.deposit_amount)}:</span>
                      <button data-testid={`admin-order-deposit-toggle-${o.id}`}
                        onClick={() => setPayment(o.id, { deposit_status: o.deposit_status === "paid" ? "pending" : "paid" })}
                        className={`px-3 py-1 rounded-full text-xs border ${o.deposit_status === "paid" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" : "bg-ink-700 text-amber-300 border-amber-500/40"}`}>
                        {o.deposit_status === "paid" ? "Paid — mark unpaid" : "Mark paid"}
                      </button>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-cream/80">Balance {currency(o.balance_amount)}:</span>
                      <button data-testid={`admin-order-balance-toggle-${o.id}`}
                        onClick={() => setPayment(o.id, { balance_status: o.balance_status === "paid" ? "pending" : "paid" })}
                        className={`px-3 py-1 rounded-full text-xs border ${o.balance_status === "paid" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" : "bg-ink-700 text-amber-300 border-amber-500/40"}`}>
                        {o.balance_status === "paid" ? "Paid — mark unpaid" : "Mark paid"}
                      </button>
                    </div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </div>
  );
}
